import React, { useEffect, useState, useMemo } from 'react';
import { DateRangePicker } from 'rsuite';
import Loader from '../../components/Loader';
import BarChart from '../../components/BarChart';
import { DateRange } from 'rsuite/esm/DateRangePicker/types';
import { User } from '../../interfaces/User';
import { Button, Row } from 'react-bootstrap';
import './dashboard-graph.scss';
import Select, { SingleValue } from 'react-select';
import LaddaButton, { EXPAND_LEFT } from 'react-ladda-button';
import { downloadDashboardData, getData, getEnums } from '../../utils/api/business-dashboard';
import { getAllBots } from '../../utils/api/automated-messages';
import {
    CampaignTypes,
    PensionMeetingTypes,
    GroupBy,
    Days,
    DashboardExtraStatuses,
    SourceTypes,
    PensionTransferredOptions,
    MeetingAttendance,
    AgeRanges,
    Genders,
    SalaryRanges,
    MaritalStatuses,
    PensionSignDays,
    PensionFormsLinkStatus,
    TargetAudienceStatuses,
} from './consts';
import { camelCaseToText } from '../../utils/camelCaseToText';
import { getAbTestsVariations } from '../../utils/api/abTests';
import { getAllCampaignNames } from '../../utils/api/clients';
import globalErrorHandler from '../../utils/globalErrorHandler';
import { APICore } from '../../utils/api/apiCore';
import Modules from '../../utils/modules';

interface FilterSelectProps {
    value: string;
    onChange: (value: string) => void;
    options: Array<{ label: string; value: string }> | Array<{ label: string; options: Array<{ label: string; value: string }> }>;
    placeholder: string;
    label?: string;
    minWidth?: number;
    onInputChange?: (value: string) => void;
    getValueLabel?: (value: string) => string;
}

const FilterSelect: React.FC<FilterSelectProps> = ({
    value,
    onChange,
    options,
    placeholder,
    label,
    minWidth = 180,
    onInputChange,
    getValueLabel,
}) => {
    const getValue = () => {
        if (!value) return { label: label || placeholder, value: '' };

        if (getValueLabel) {
            const labelValue = getValueLabel(value);
            return { label: labelValue || placeholder, value };
        }

        // Find the option that matches the value
        const findOption = (opts: any[]): { label: string; value: string } | null => {
            for (const opt of opts) {
                if (opt.options) {
                    const found = findOption(opt.options);
                    if (found) return found;
                } else if (opt.value === value) {
                    return opt;
                }
            }
            return null;
        };

        const found = findOption(options as any[]);
        return found || { label: label || placeholder, value: '' };
    };

    return (
        <Select
            className="react-select-container"
            classNamePrefix="react-select"
            value={getValue()}
            onChange={(selected) => onChange(selected?.value || '')}
            options={options}
            placeholder={placeholder}
            isSearchable
            onInputChange={onInputChange}
            styles={{
                control: (base) => ({
                    ...base,
                    minWidth,
                    marginRight: 10,
                    marginBottom: 10,
                }),
            }}
        />
    );
};

interface DashboardGraphProps {
    users?: User[];
    groupBy?: string;
    title: string;
    startDate?: Date;
    endDate?: Date;
    dynamicGraph?: boolean;
    filterBySource?: boolean;
    startStatus?: string;
    endStatus?: string;
}

const DashboardGraph = (props: DashboardGraphProps) => {
    const { users, title, dynamicGraph } = props;
    const [data, setData] = useState<any>(null);
    const [startDate, setStartDate] = useState<Date | null>(
        props.startDate || new Date(`${new Date().getFullYear()}-${new Date().getMonth() + 1}-01`),
    );
    const [endDate, setEndDate] = useState<Date | null>(props.endDate || new Date());
    const [endStatusStartDate, setEndStatusStartDate] = useState<Date | null>(null);
    const [endStatusEndDate, setEndStatusEndDate] = useState<Date | null>(null);
    const [onboardingTimeOfDay, setOnboardingTimeOfDay] = useState<string>('');
    const [onboardingDayOfWeek, setOnboardingDayOfWeek] = useState<string>('');
    const [targetAudienceStatus, setTargetAudienceStatus] = useState<string>('');
    const [pensionTimeOfDay, setPensionTimeOfDay] = useState<string>('');
    const [pensionDayOfWeek, setPensionDayOfWeek] = useState<string>('');
    const [onboardingRep, setOnboardingRep] = useState<User>();
    const [pensionMeetingType, setPensionMeetingType] = useState<string>('');
    const [pensionAgent, setPensionAgent] = useState<User>();
    const [insuranceAgent, setInsuranceAgent] = useState<User>();
    const [dynamicGraphFilterView, setDynamicGraphFilterView] = useState<boolean>(dynamicGraph || false);
    const [selectedFilterKeys, setSelectedFilterKeys] = useState<string[]>([]);
    const [registrationToOnboarding, setRegistrationToOnboarding] = useState<string>('');
    const [savingAmount, setSavingAmount] = useState<string>('');
    const [groupBy, setGroupBy] = useState<string>(props.groupBy || GroupBy.day);
    const [startStatus, setStartStatus] = useState<string>(props.startStatus || 'onboarding_preZoom');
    const [endStatus, setEndStatus] = useState<string>(props.endStatus || 'onboarding_postZoom');
    const [source, setSource] = useState<string>('');
    const [pensionTransferred, setPensionTransferred] = useState<string>('');
    const [onboardingMeetingAttendance, setOnboardingMeetingAttendance] = useState<string>('');
    const [pensionMeetingAttendance, setPensionMeetingAttendance] = useState<string>('');
    const { user } = new APICore().getLoggedInUser();

    const [module, setModule] = useState<string>('');
    const [age, setAge] = useState<string>('');
    const [salary, setSalary] = useState<string>('');
    const [maritalStatus, setMaritalStatus] = useState<string>('');
    const [gender, setGender] = useState<string>('');
    const [testName, setTestName] = useState<string>('');
    const [testValue, setTestValue] = useState<string>('');
    const [campaignName, setCampaignName] = useState<string>('');
    const [pensionSignDays, setPensionSignDays] = useState<string>('');
    const [isDownloading, setIsDownloading] = useState(false);
    const [pensionFormsLinkStatus, setPensionFormsLinkStatus] = useState<string>('');
    const [botName, setBotName] = useState<string>('');
    const [availableBots, setAvailableBots] = useState<Array<{ name: string }>>([]);

    const [onboardingStatuses, setOnboardingStatuses] = useState<string[]>([]);
    const [pensionStatuses, setPensionStatuses] = useState<string[]>([]);
    const [insuranceStatuses, setInsuranceStatuses] = useState<string[]>([]);
    const [taxReturnStatuses, setTaxReturnStatuses] = useState<string[]>([]);
    const [mortgageStatuses, setMortgageStatuses] = useState<string[]>([]);
    const [investmentStatuses, setInvestmentStatuses] = useState<string[]>([]);
    const [bankFeesStatuses, setBankFeesStatuses] = useState<string[]>([]);
    const [loanStatuses, setLoanStatuses] = useState<string[]>([]);
    const [depositStatuses, setDepositStatuses] = useState<string[]>([]);
    const [abTestVariations, setAbTestVariations] = useState<{
        [key: string]: string[];
    }>({});
    const [yLabels, setYLabels] = useState<string[]>(['', '']);
    const [campaignNames, setCampaignNames] = useState<string[]>([]);
    const [campaignSearch, setCampaignSearch] = useState('');
    const [filteredCampaignNames, setFilteredCampaignNames] = useState(campaignNames);

    const [isLoading, setIsLoading] = useState(false);

    const handleData = async () => {
        setIsLoading(true);
        // Only send start date range if end status date range is not set
        const useStartDateRange = !endStatusStartDate || !endStatusEndDate;
        const params = {
            startDate: useStartDateRange && startDate ? startDate.toISOString() : undefined,
            endDate: useStartDateRange && endDate ? endDate.toISOString() : undefined,
            startStatus: startStatus.split('_')[1],
            startStatusModule: startStatus.split('_')[0],
            endStatus: endStatus.split('_')[1],
            endStatusModule: endStatus.split('_')[0],
            onboardingTimeOfDay,
            onboardingDayOfWeek,
            pensionMeetingType,
            targetAudienceStatus,
            pensionTimeOfDay,
            pensionDayOfWeek,
            onboardingRep: onboardingRep?._id || '',
            pensionAgent: pensionAgent?._id || '',
            insuranceAgent: insuranceAgent?._id || '',
            registrationToOnboarding,
            savingAmount,
            source,
            pensionTransferred,
            onboardingMeetingAttendance,
            pensionMeetingAttendance,
            preferredModule: module,
            age,
            salary,
            maritalStatus,
            gender,
            testName,
            testValue,
            groupBy,
            campaignName,
            pensionSignDays: encodeURIComponent(pensionSignDays),
            pensionFormsLinkStatus,
            botName,
            endStatusStartDate: endStatusStartDate?.toISOString(),
            endStatusEndDate: endStatusEndDate?.toISOString(),
        };

        try {
            const data = await getData(params);
            setData(data);
            setIsLoading(false);

            const y1Label = camelCaseToText(startStatus.split('_')[1]);
            const y2Label = camelCaseToText(endStatus.split('_')[1]);
            setYLabels([y1Label, y1Label !== y2Label ? y2Label : y2Label + '*']);

            if (dynamicGraph && !onboardingStatuses.length && !pensionStatuses.length && !Object.keys(abTestVariations).length) {
                const [enumsResult, abTestResult, campaignResult] = await Promise.all([getEnums(), getAbTestsVariations(), getAllCampaignNames()]);

                setOnboardingStatuses([
                    DashboardExtraStatuses.onboardingDate,
                    DashboardExtraStatuses.onboardingCallShow,
                    ...Object.keys(enumsResult.OnboardingStatuses || {}),
                ]);
                setPensionStatuses([
                    DashboardExtraStatuses.pensionAgentCallDate,
                    DashboardExtraStatuses.pensionAgentCallShow,
                    DashboardExtraStatuses.signedOneModule,
                    ...Object.keys(enumsResult.PensionStatuses || {}),
                ]);
                setInsuranceStatuses(Object.keys(enumsResult.InsuranceStatuses || {}));
                setTaxReturnStatuses(Object.keys(enumsResult.TaxReturnStatus || {}));
                setMortgageStatuses(Object.keys(enumsResult.MortgageStatuses || {}));
                setInvestmentStatuses(Object.keys(enumsResult.InvestmentStatuses || {}));
                setBankFeesStatuses(Object.keys(enumsResult.BankFeesStatuses || {}));
                setLoanStatuses(Object.keys(enumsResult.LoanStatuses || {}));
                setDepositStatuses(Object.keys(enumsResult.DepositStatuses || {}));

                setAbTestVariations(abTestResult.variations);
                setCampaignNames(campaignResult.campaignNames);
            }
        } catch (error) {
            setIsLoading(false);
            globalErrorHandler(error);
        }
    };

    const downloadData = async () => {
        setIsDownloading(true);

        // Only send start date range if end status date range is not set
        const useStartDateRange = !endStatusStartDate || !endStatusEndDate;
        const params = {
            startDate: useStartDateRange && startDate ? startDate.toISOString() : undefined,
            endDate: useStartDateRange && endDate ? endDate.toISOString() : undefined,
            startStatus: startStatus.split('_')[1],
            startStatusModule: startStatus.split('_')[0],
            endStatus: endStatus.split('_')[1],
            endStatusModule: endStatus.split('_')[0],
            onboardingTimeOfDay,
            onboardingDayOfWeek,
            preferredModule: module,
            targetAudienceStatus,
            pensionTimeOfDay,
            pensionDayOfWeek,
            onboardingRep: onboardingRep?._id || '',
            pensionAgent: pensionAgent?._id || '',
            insuranceAgent: insuranceAgent?._id || '',
            registrationToOnboarding,
            savingAmount,
            source,
            pensionTransferred,
            onboardingMeetingAttendance,
            pensionMeetingAttendance,
            age,
            salary,
            maritalStatus,
            gender,
            testName,
            testValue,
            groupBy,
            campaignName,
            pensionSignDays: encodeURIComponent(pensionSignDays),
            pensionFormsLinkStatus,
            botName,
            endStatusStartDate: endStatusStartDate?.toISOString(),
            endStatusEndDate: endStatusEndDate?.toISOString(),
        };

        try {
            const result = await downloadDashboardData(params);
            setIsDownloading(false);
            const blob = await result.blob();
            const url = window.URL.createObjectURL(new Blob([blob], { type: blob.type }));
            const link = document.createElement('a');
            link.href = url;

            link.setAttribute('download', `dashboard-data.xlsx`);
            document.body.appendChild(link);
            link.click();
            link.parentNode?.removeChild(link);
        } catch (error) {
            setIsDownloading(false);
            globalErrorHandler(error);
        }
    };

    useEffect(() => {
        handleData();
    }, []);

    useEffect(() => {
        const loadBots = async () => {
            if (!availableBots.length) {
                try {
                    const botsResponse = await getAllBots();
                    setAvailableBots(botsResponse.bots || []);
                } catch (error) {
                    console.error('Failed to load bots', error);
                }
            }
        };
        loadBots();
    }, []);

    useEffect(() => {
        const filtered = campaignNames.filter((option) => option?.toLowerCase().includes(campaignSearch?.toLowerCase()));
        setFilteredCampaignNames(filtered);
    }, [campaignSearch, campaignNames]);

    const onOk = (date: DateRange) => {
        if (date) {
            setStartDate(date[0]);
            setEndDate(date[1]);
        }
    };

    const onStartDateClean = () => {
        setStartDate(null);
        setEndDate(null);
    };

    const onEndStatusDateOk = (date: DateRange) => {
        if (date) {
            setEndStatusStartDate(date[0]);
            setEndStatusEndDate(date[1]);
        }
    };

    const onEndStatusDateClean = () => {
        setEndStatusStartDate(null);
        setEndStatusEndDate(null);
    };

    const loadFilterData = async () => {
        if (!onboardingStatuses.length || !pensionStatuses.length || !Object.keys(abTestVariations).length) {
            const [enumsResult, abTestResult, campaignResult] = await Promise.all([getEnums(), getAbTestsVariations(), getAllCampaignNames()]);

            setOnboardingStatuses([
                DashboardExtraStatuses.onboardingDate,
                DashboardExtraStatuses.onboardingCallShow,
                ...Object.keys(enumsResult.OnboardingStatuses || {}),
            ]);
            setPensionStatuses([
                DashboardExtraStatuses.pensionAgentCallDate,
                DashboardExtraStatuses.pensionAgentCallShow,
                ...Object.keys(enumsResult.PensionStatuses || {}),
            ]);
            setInsuranceStatuses(Object.keys(enumsResult.InsuranceStatuses || {}));
            setTaxReturnStatuses(Object.keys(enumsResult.TaxReturnStatus || {}));
            setMortgageStatuses(Object.keys(enumsResult.MortgageStatuses || {}));
            setInvestmentStatuses(Object.keys(enumsResult.InvestmentStatuses || {}));
            setBankFeesStatuses(Object.keys(enumsResult.BankFeesStatuses || {}));
            setLoanStatuses(Object.keys(enumsResult.LoanStatuses || {}));
            setDepositStatuses(Object.keys(enumsResult.DepositStatuses || {}));

            setAbTestVariations(abTestResult.variations);
            setCampaignNames(campaignResult.campaignNames);
        }
    };

    // Load filter data on mount
    useEffect(() => {
        loadFilterData();
    }, []);

    const getTitle = () => {
        if (startStatus === props.startStatus && endStatus === props.endStatus) {
            return title;
        }

        return `${camelCaseToText(startStatus.split('_')[1])} to ${camelCaseToText(endStatus.split('_')[1])}`;
    };

    const relevantModules = [Modules.pension, Modules.taxReturn, Modules.insurance, Modules.mortgage, Modules.bankFees, Modules.investment];

    // Helper functions to build options
    const buildStatusOptions = () => {
        const options: Array<{ label: string; options: Array<{ label: string; value: string }> }> = [
            {
                label: 'Onboarding',
                options: onboardingStatuses.map((status) => ({
                    label: camelCaseToText(status),
                    value: `onboarding_${status}`,
                })),
            },
            {
                label: 'Pension',
                options: pensionStatuses.map((status) => ({
                    label: camelCaseToText(status),
                    value: `pension_${status}`,
                })),
            },
            {
                label: 'Insurance',
                options: insuranceStatuses.map((status) => ({
                    label: camelCaseToText(status),
                    value: `insurance_${status}`,
                })),
            },
            {
                label: 'Tax Return',
                options: taxReturnStatuses.map((status) => ({
                    label: camelCaseToText(status),
                    value: `taxReturn_${status}`,
                })),
            },
            {
                label: 'Mortgage',
                options: mortgageStatuses.map((status) => ({
                    label: camelCaseToText(status),
                    value: `mortgage_${status}`,
                })),
            },
            {
                label: 'Investment',
                options: investmentStatuses.map((status) => ({
                    label: camelCaseToText(status),
                    value: `investment_${status}`,
                })),
            },
            {
                label: 'Bank Fees',
                options: bankFeesStatuses.map((status) => ({
                    label: camelCaseToText(status),
                    value: `bankFees_${status}`,
                })),
            },
        ];
        return options.filter((group) => group.options.length > 0);
    };

    const buildOptionsWithAll = (items: readonly string[] | string[], transformLabel?: (item: string) => string) => {
        return [
            { label: 'All', value: '' },
            ...items.map((item) => ({
                label: transformLabel ? transformLabel(item) : item,
                value: item,
            })),
        ];
    };

    const buildOptionsFromObject = (obj: Record<string, string>, transformLabel?: (item: string) => string) => {
        return buildOptionsWithAll(Object.values(obj), transformLabel);
    };

    const getStatusLabel = (status: string, prefix: string = '') => {
        const parts = status.split('_');
        return prefix ? `${prefix}: ${camelCaseToText(parts[1] || parts[0])}` : camelCaseToText(parts[1] || parts[0]);
    };

    // Filter definitions - each filter has a key, label, and render function
    const filterDefinitions = useMemo(() => {
        const defs: Record<
            string,
            {
                label: string;
                render: () => React.ReactNode;
            }
        > = {
            onboardingRep: {
                label: 'Onboarding Rep',
                render: () =>
                    users ? (
                        <FilterSelect
                            value={onboardingRep?._id || ''}
                            onChange={(userId) => {
                                if (userId && userId !== '') {
                                    const user = users?.find((u: User) => u._id === userId);
                                    setOnboardingRep(user);
                                    setPensionAgent(undefined);
                                    setInsuranceAgent(undefined);
                                } else {
                                    setOnboardingRep(undefined);
                                }
                            }}
                            options={[
                                { label: 'All', value: '' },
                                ...users.filter((u: User) => u.roles.includes('interviewer')).map((u) => ({ label: u.fullName || '', value: u._id })),
                            ]}
                            placeholder="Onboarding Rep"
                            getValueLabel={(userId) => users.find((u: User) => u._id === userId)?.fullName || 'Onboarding Rep'}
                            minWidth={160}
                        />
                    ) : null,
            },
            pensionAgent: {
                label: 'Pension Agent',
                render: () =>
                    users ? (
                        <FilterSelect
                            value={pensionAgent?._id || ''}
                            onChange={(userId) => {
                                if (userId && userId !== '') {
                                    const user = users?.find((u: User) => u._id === userId);
                                    setPensionAgent(user);
                                    setOnboardingRep(undefined);
                                    setInsuranceAgent(undefined);
                                } else {
                                    setPensionAgent(undefined);
                                }
                            }}
                            options={[
                                { label: 'All', value: '' },
                                ...users
                                    .filter((u: User) => u.roles.includes('agent') && u.agentModules?.includes('pension'))
                                    .map((u) => ({ label: u.fullName || '', value: u._id })),
                            ]}
                            placeholder="Pension Agent"
                            getValueLabel={(userId) => users.find((u: User) => u._id === userId)?.fullName || 'Pension Agent'}
                            minWidth={160}
                        />
                    ) : null,
            },
            insuranceAgent: {
                label: 'Insurance Agent',
                render: () =>
                    users ? (
                        <FilterSelect
                            value={insuranceAgent?._id || ''}
                            onChange={(userId) => {
                                if (userId && userId !== '') {
                                    const user = users?.find((u: User) => u._id === userId);
                                    setInsuranceAgent(user);
                                    setPensionAgent(undefined);
                                    setOnboardingRep(undefined);
                                } else {
                                    setInsuranceAgent(undefined);
                                }
                            }}
                            options={[
                                { label: 'All', value: '' },
                                ...users
                                    .filter((u: User) => u.roles.includes('agent') && u.agentModules?.includes('insurance'))
                                    .map((u) => ({ label: u.fullName || '', value: u._id })),
                            ]}
                            placeholder="Insurance Agent"
                            getValueLabel={(userId) => users.find((u: User) => u._id === userId)?.fullName || 'Insurance Agent'}
                            minWidth={160}
                        />
                    ) : null,
            },
            preferredModule: {
                label: 'Preferred Module',
                render: () => (
                    <FilterSelect
                        value={module}
                        onChange={setModule}
                        options={buildOptionsWithAll(relevantModules, camelCaseToText)}
                        placeholder="Preferred Module"
                        minWidth={160}
                    />
                ),
            },
            targetAudience: {
                label: 'Target Audience',
                render: () => (
                    <FilterSelect
                        value={targetAudienceStatus}
                        onChange={setTargetAudienceStatus}
                        options={buildOptionsWithAll(TargetAudienceStatuses)}
                        placeholder="Target Audience"
                        minWidth={160}
                    />
                ),
            },
            onboardingTimeOfDay: {
                label: 'Onboarding Time',
                render: () => (
                    <FilterSelect
                        value={onboardingTimeOfDay}
                        onChange={setOnboardingTimeOfDay}
                        options={[
                            { label: 'All', value: '' },
                            { label: 'Morning (8:00-10:00)', value: 'morning' },
                            { label: 'Noon (10:00-12:00)', value: 'noon' },
                            { label: 'Afternoon (12:00-15:00)', value: 'afternoon' },
                            { label: 'Evening (15:00-24:00)', value: 'evening' },
                        ]}
                        placeholder="Onboarding Time"
                        minWidth={160}
                    />
                ),
            },
            onboardingDayOfWeek: {
                label: 'Onboarding Day',
                render: () => (
                    <FilterSelect
                        value={onboardingDayOfWeek}
                        onChange={setOnboardingDayOfWeek}
                        options={[{ label: 'All', value: '' }, ...Array.from(Days).map((day, index) => ({ label: day, value: String(index) }))]}
                        placeholder="Onboarding Day"
                        label={onboardingDayOfWeek !== '' ? Days[Number(onboardingDayOfWeek)] : undefined}
                        minWidth={160}
                    />
                ),
            },
            pensionTimeOfDay: {
                label: 'Pension Call Time',
                render: () => (
                    <FilterSelect
                        value={pensionTimeOfDay}
                        onChange={setPensionTimeOfDay}
                        options={[
                            { label: 'All', value: '' },
                            { label: 'Morning (8:00-10:00)', value: 'morning' },
                            { label: 'Noon (10:00-12:00)', value: 'noon' },
                            { label: 'Afternoon (12:00-15:00)', value: 'afternoon' },
                            { label: 'Evening (15:00-24:00)', value: 'evening' },
                        ]}
                        placeholder="Pension Call Time"
                        minWidth={160}
                    />
                ),
            },
            pensionMeetingType: {
                label: 'Pension Meeting Type',
                render: () => (
                    <FilterSelect
                        value={pensionMeetingType}
                        onChange={setPensionMeetingType}
                        options={buildOptionsFromObject(PensionMeetingTypes, camelCaseToText)}
                        placeholder="Meeting Type"
                        minWidth={160}
                    />
                ),
            },
            pensionDayOfWeek: {
                label: 'Pension Call Day',
                render: () => (
                    <FilterSelect
                        value={pensionDayOfWeek}
                        onChange={setPensionDayOfWeek}
                        options={[{ label: 'All', value: '' }, ...Array.from(Days).map((day, index) => ({ label: day, value: String(index) }))]}
                        placeholder="Pension Call Day"
                        label={pensionDayOfWeek !== '' ? Days[Number(pensionDayOfWeek)] : undefined}
                        minWidth={160}
                    />
                ),
            },
            pensionTransferred: {
                label: 'Pension Transferred',
                render: () => (
                    <FilterSelect
                        value={pensionTransferred}
                        onChange={setPensionTransferred}
                        options={buildOptionsWithAll(PensionTransferredOptions, camelCaseToText)}
                        placeholder="Pension Transferred"
                        minWidth={160}
                    />
                ),
            },
            registrationToOnboarding: {
                label: 'Reg. to Onboarding Days',
                render: () => (
                    <FilterSelect
                        value={registrationToOnboarding}
                        onChange={setRegistrationToOnboarding}
                        options={[
                            { label: 'All', value: '' },
                            { label: '0-1 Days', value: 'zeroToOneDay' },
                            { label: '1-2 Days', value: 'oneToTwoDays' },
                            { label: '2-4 Days', value: 'twoToFourDays' },
                            { label: '4-7 Days', value: 'fourToSevenDays' },
                            { label: '7-14 Days', value: 'sevenToFourteenDays' },
                            { label: '14-21 Days', value: 'fourteenToTwentyOneDays' },
                            { label: '21+ Days', value: 'twentyOnePlusDays' },
                        ]}
                        placeholder="Reg. to Ob. Days"
                        minWidth={160}
                    />
                ),
            },
            savingAmount: {
                label: 'Saving Amount',
                render: () => (
                    <FilterSelect
                        value={savingAmount}
                        onChange={setSavingAmount}
                        options={[
                            { label: 'All', value: '' },
                            { label: '0', value: 'zero' },
                            { label: '1-10,000', value: 'oneToTenThousand' },
                            { label: '10,000-100,000', value: 'tenToHundredThousand' },
                            { label: '100,000-250,000', value: 'hundredToTwoHundredFiftyThousand' },
                            { label: '250,000-500,000', value: 'twoHundredFiftyToFiveHundredThousand' },
                            { label: '500,000+', value: 'fiveHundredThousandPlus' },
                        ]}
                        placeholder="Saving Amount"
                        minWidth={160}
                    />
                ),
            },
            source: {
                label: 'Source',
                render: () => (
                    <FilterSelect
                        value={source}
                        onChange={setSource}
                        options={buildOptionsWithAll([...Object.values(CampaignTypes), ...Object.values(SourceTypes)])}
                        placeholder="Source"
                        minWidth={160}
                    />
                ),
            },
            onboardingMeetingAttendance: {
                label: 'Onboarding Attendance',
                render: () => (
                    <FilterSelect
                        value={onboardingMeetingAttendance}
                        onChange={setOnboardingMeetingAttendance}
                        options={buildOptionsWithAll(MeetingAttendance, camelCaseToText)}
                        placeholder="Ob. Attendance"
                        minWidth={160}
                    />
                ),
            },
            pensionMeetingAttendance: {
                label: 'Pension Attendance',
                render: () => (
                    <FilterSelect
                        value={pensionMeetingAttendance}
                        onChange={setPensionMeetingAttendance}
                        options={buildOptionsWithAll(MeetingAttendance, camelCaseToText)}
                        placeholder="Pension Attendance"
                        minWidth={160}
                    />
                ),
            },
            age: {
                label: 'Age',
                render: () => (
                    <FilterSelect
                        value={age}
                        onChange={setAge}
                        options={buildOptionsFromObject(AgeRanges, camelCaseToText)}
                        placeholder="Age"
                        minWidth={140}
                    />
                ),
            },
            salary: {
                label: 'Salary',
                render: () => (
                    <FilterSelect
                        value={salary}
                        onChange={setSalary}
                        options={buildOptionsFromObject(SalaryRanges, camelCaseToText)}
                        placeholder="Salary"
                        minWidth={140}
                    />
                ),
            },
            gender: {
                label: 'Gender',
                render: () => (
                    <FilterSelect
                        value={gender}
                        onChange={setGender}
                        options={buildOptionsFromObject(Genders, camelCaseToText)}
                        placeholder="Gender"
                        minWidth={140}
                    />
                ),
            },
            maritalStatus: {
                label: 'Marital Status',
                render: () => (
                    <FilterSelect
                        value={maritalStatus}
                        onChange={setMaritalStatus}
                        options={buildOptionsFromObject(MaritalStatuses, camelCaseToText)}
                        placeholder="Marital Status"
                        minWidth={140}
                    />
                ),
            },
            abTest: {
                label: 'AB Test',
                render: () => (
                    <FilterSelect
                        value={testName && testValue ? `${testName}:${testValue}` : ''}
                        onChange={(value) => {
                            if (value) {
                                const [name, val] = value.split(':');
                                setTestName(name);
                                setTestValue(val);
                            } else {
                                setTestName('');
                                setTestValue('');
                            }
                        }}
                        options={[
                            { label: 'All', value: '' },
                            ...Object.entries(abTestVariations).flatMap(([tn, values]) =>
                                values.map((v) => ({
                                    label: `${camelCaseToText(tn)}: ${v}`,
                                    value: `${tn}:${v}`,
                                })),
                            ),
                        ]}
                        placeholder="AB Test"
                        label={testName && testValue ? `${camelCaseToText(testName)}: ${testValue}` : undefined}
                        minWidth={160}
                    />
                ),
            },
            campaignName: {
                label: 'Campaign Name',
                render: () => (
                    <FilterSelect
                        value={campaignName}
                        onChange={setCampaignName}
                        options={buildOptionsWithAll(filteredCampaignNames, camelCaseToText)}
                        placeholder="Campaign Name"
                        onInputChange={setCampaignSearch}
                        minWidth={160}
                    />
                ),
            },
            pensionSignDays: {
                label: 'Pension Sign Days',
                render: () => (
                    <FilterSelect
                        value={pensionSignDays}
                        onChange={setPensionSignDays}
                        options={buildOptionsFromObject(PensionSignDays)}
                        placeholder="Sign Days"
                        label={pensionSignDays ? `Sign Days: ${pensionSignDays}` : undefined}
                        minWidth={140}
                    />
                ),
            },
            pensionFormsLinkStatus: {
                label: 'Forms Link Status',
                render: () => (
                    <FilterSelect
                        value={pensionFormsLinkStatus}
                        onChange={setPensionFormsLinkStatus}
                        options={buildOptionsFromObject(PensionFormsLinkStatus)}
                        placeholder="Forms Link Status"
                        minWidth={160}
                    />
                ),
            },
            botName: {
                label: 'Assigned Bot',
                render: () => (
                    <FilterSelect
                        value={botName}
                        onChange={setBotName}
                        options={buildOptionsWithAll(availableBots.map((bot) => bot.name))}
                        placeholder="Assigned Bot"
                        label={botName ? `Bot: ${botName}` : undefined}
                        minWidth={140}
                    />
                ),
            },
        };
        return defs;
    }, [
        users,
        onboardingRep,
        pensionAgent,
        insuranceAgent,
        module,
        targetAudienceStatus,
        onboardingTimeOfDay,
        onboardingDayOfWeek,
        pensionTimeOfDay,
        pensionMeetingType,
        pensionDayOfWeek,
        pensionTransferred,
        registrationToOnboarding,
        savingAmount,
        source,
        onboardingMeetingAttendance,
        pensionMeetingAttendance,
        age,
        salary,
        gender,
        maritalStatus,
        testName,
        testValue,
        abTestVariations,
        campaignName,
        filteredCampaignNames,
        pensionSignDays,
        pensionFormsLinkStatus,
        botName,
        availableBots,
        relevantModules,
    ]);

    const availableFilterOptions = useMemo(
        () =>
            Object.entries(filterDefinitions)
                .filter(([key]) => !selectedFilterKeys.includes(key))
                .map(([key, def]) => ({
                    value: key,
                    label: def.label,
                })),
        [filterDefinitions, selectedFilterKeys],
    );

    const handleAddFilter = (selected: SingleValue<{ value: string; label: string }>) => {
        if (selected && !selectedFilterKeys.includes(selected.value)) {
            setSelectedFilterKeys([...selectedFilterKeys, selected.value]);
        }
    };

    const handleRemoveFilter = (key: string) => {
        setSelectedFilterKeys(selectedFilterKeys.filter((k) => k !== key));
    };

    return (
        <div className="dashboard-graph">
            <div>
                <Row>
                    <div className="filter-controls">
                        <div className="filter-row main-filters">
                            <div className="filter-group start-status-group">
                                <span className="filter-group-label">Start Status</span>
                                <div className="filter-group-content">
                                    {dynamicGraphFilterView && (
                                        <FilterSelect
                                            value={startStatus}
                                            onChange={setStartStatus}
                                            options={buildStatusOptions()}
                                            placeholder="Status..."
                                            label={getStatusLabel(startStatus)}
                                            minWidth={160}
                                        />
                                    )}
                                    <DateRangePicker
                                        format="dd/MM/yyyy"
                                        placeholder="Date Range"
                                        value={startDate && endDate ? [startDate, endDate] : null}
                                        onOk={onOk}
                                        onClean={onStartDateClean}
                                    />
                                </div>
                            </div>

                            <div className="filter-group end-status-group">
                                <span className="filter-group-label">End Status</span>
                                <div className="filter-group-content">
                                    {dynamicGraphFilterView && (
                                        <FilterSelect
                                            value={endStatus}
                                            onChange={setEndStatus}
                                            options={buildStatusOptions()}
                                            placeholder="Status..."
                                            label={getStatusLabel(endStatus)}
                                            minWidth={160}
                                        />
                                    )}
                                    <DateRangePicker
                                        format="dd/MM/yyyy"
                                        placeholder="Date Range (optional)"
                                        value={endStatusStartDate && endStatusEndDate ? [endStatusStartDate, endStatusEndDate] : null}
                                        onOk={onEndStatusDateOk}
                                        onClean={onEndStatusDateClean}
                                    />
                                </div>
                            </div>

                            <div className="filter-group grouping-group">
                                <span className="filter-group-label">Group By</span>
                                <div className="filter-group-content">
                                    <FilterSelect
                                        value={groupBy}
                                        onChange={setGroupBy}
                                        options={Object.entries(GroupBy).map(([key, value]) => ({
                                            label: camelCaseToText(key),
                                            value,
                                        }))}
                                        placeholder="Group by..."
                                        label={camelCaseToText(
                                            Object.keys(GroupBy)[Object.values(GroupBy).indexOf(groupBy as (typeof GroupBy)[keyof typeof GroupBy])] ||
                                                'daily',
                                        )}
                                        minWidth={140}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="filter-row selected-filters">
                            <Select
                                className="filter-selector"
                                classNamePrefix="react-select"
                                value={null}
                                onChange={handleAddFilter}
                                options={availableFilterOptions}
                                placeholder="+ Add filter..."
                                styles={{
                                    control: (base) => ({
                                        ...base,
                                        minWidth: 160,
                                        fontSize: 13,
                                        marginBottom: '10px',
                                    }),
                                }}
                            />
                            {selectedFilterKeys.map((key) => (
                                <div key={key} className="selected-filter-item">
                                    {filterDefinitions[key]?.render()}
                                    <button type="button" className="remove-filter-btn" onClick={() => handleRemoveFilter(key)} title="Remove filter">
                                        <i className="mdi mdi-close" />
                                    </button>
                                </div>
                            ))}
                            <Button onClick={handleData} className="apply-button btn-sm" variant="primary" style={{ marginBottom: '10px' }}>
                                Apply
                            </Button>
                        </div>

                        {!dynamicGraphFilterView && (
                            <div className="filter-row status-filters">
                                <FilterSelect
                                    value={startStatus}
                                    onChange={setStartStatus}
                                    options={buildStatusOptions()}
                                    placeholder="Start Status..."
                                    label={getStatusLabel(startStatus, 'From')}
                                    minWidth={180}
                                />
                                <FilterSelect
                                    value={endStatus}
                                    onChange={setEndStatus}
                                    options={buildStatusOptions()}
                                    placeholder="End Status..."
                                    label={getStatusLabel(endStatus, 'To')}
                                    minWidth={180}
                                />
                            </div>
                        )}
                    </div>
                </Row>
            </div>

            {data ? (
                <div className="chart-wrapper">
                    {user?.isAdmin && !isLoading && (
                        <LaddaButton
                            loading={isDownloading}
                            data-style={EXPAND_LEFT}
                            className="download-btn ladda-button btn btn-primary btn btn-info chat-send waves-effect waves-light btn-sm"
                            dir="ltr"
                            onClick={downloadData}
                        >
                            {!isDownloading ? <i className="mdi mdi-download me-1" /> : null} Download
                        </LaddaButton>
                    )}
                    {isLoading && <Loader />}
                    <BarChart title={getTitle()} yLabels={yLabels} data={data} />
                </div>
            ) : null}
        </div>
    );
};

export default DashboardGraph;
